import React, { createContext, useContext } from 'react';

import { Request } from '../../../codegen/graphql';
import {
    getPriceChangeScope,
    getSuggestedStoreIds,
    PRICE_CHANGE_SCOPE,
} from '../../../helpers/priceChangeScope';
import {
    useProductSalesScope,
    UseProductSalesScopeResult,
} from '../../../hooks/useProductSalesScope';

const BatchSalesScopeContext = createContext<UseProductSalesScopeResult | null>(
    null,
);

type ProviderProps = Readonly<
    React.PropsWithChildren<{
        requests: Request[];
        advertisingZoneId?: string | null;
    }>
>;

export function BatchSalesScopeProvider({
    requests,
    advertisingZoneId,
    children,
}: ProviderProps) {
    const requestWithZone =
        requests.find(request => Boolean(request.advertisingZoneId)) ??
        requests[0];
    const requestScope = getPriceChangeScope(requestWithZone);
    const zoneId =
        advertisingZoneId ?? requestWithZone?.advertisingZoneId ?? undefined;
    let priceChangeScope = requestScope ?? PRICE_CHANGE_SCOPE.STORE;
    if (requestScope === PRICE_CHANGE_SCOPE.SPECIFIC_STORES) {
        priceChangeScope = requestScope;
    } else if (zoneId) {
        priceChangeScope = PRICE_CHANGE_SCOPE.ZONE;
    }

    const salesScope = useProductSalesScope({
        advertisingZoneId: zoneId,
        priceChangeScope,
        suggestedStoreIds: getSuggestedStoreIds(requestWithZone),
        lockToRequestScope: true,
    });

    return (
        <BatchSalesScopeContext.Provider value={salesScope}>
            {children}
        </BatchSalesScopeContext.Provider>
    );
}

export function useOptionalBatchSalesScope(): UseProductSalesScopeResult | null {
    return useContext(BatchSalesScopeContext);
}

export function useBatchSalesScope(): UseProductSalesScopeResult {
    const context = useContext(BatchSalesScopeContext);
    if (!context) {
        throw new Error(
            'useBatchSalesScope must be used within a BatchSalesScopeProvider',
        );
    }
    return context;
}
